import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import api from "../../api";

const EVENTS = [
  ["CompleteRegistration", "Sign-ups"],
  ["StartTrial", "Trials started"],
  ["Subscribe", "Paid plans"],
  ["Purchase", "Purchases"],
  ["Lead", "Leads"],
];

const sum = (row = {}, keys) => keys.reduce((n, k) => n + (row[k] || 0), 0);

/**
 * Settings → System: server-side ad conversions (backend ConversionTracker). Which platforms
 * are connected (.env), what was sent in the last 30 days, and the Google Ads import file.
 */
export default function AdConversionsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api
      .get("admin/analytics/conversions", { params: { days: 30 } })
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("admin/analytics/conversions/google-ads.csv", {
        params: { days: 30 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `google-ads-conversions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the Google Ads file.");
    } finally {
      setDownloading(false);
    }
  };

  const platform = (on, name) => (
    <Chip size="small" color={on ? "success" : "default"} label={`${name}: ${on ? "connected" : "not set up"}`} />
  );

  return (
    <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Ad conversions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sign-ups, trials and payments sent from the server to Meta and TikTok, so ads can learn
          from real results. Only visitors who accepted cookies are sent. Connect a platform by
          adding its keys to the backend .env (see SERVER_SIDE_CONVERSIONS.md).
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <CircularProgress size={18} />
        ) : !data ? (
          <Typography variant="body2" color="text.secondary">Could not load conversion figures.</Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {platform(data.platforms?.meta, "Meta")}
              {platform(data.platforms?.tiktok, "TikTok")}
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Last {data.days} days</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">No cookie consent</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {EVENTS.map(([key, label]) => {
                  const row = data.events?.[key] || {};
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{sum(row, ["sent", "partial", "queued"])}</TableCell>
                      <TableCell align="right">{sum(row, ["skipped_no_consent"])}</TableCell>
                      <TableCell align="right">{sum(row, ["failed"])}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={downloadCsv}
              disabled={downloading}
            >
              Google Ads conversions (CSV)
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Trials and payments from Google ad clicks, for Google Ads → Goals → Uploads.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
